import { markRaw } from 'vue'

// Import Employment column components
import EmploymentWorkerColumn from './EmploymentWorkerColumn.vue'
import EmploymentJobColumn from './EmploymentJobColumn.vue'
import EmploymentStartDateColumn from './EmploymentStartDateColumn.vue'
import EmploymentEndDateColumn from './EmploymentEndDateColumn.vue'
import EmploymentOnboardedDateColumn from './EmploymentOnboardedDateColumn.vue'
import EmploymentPreviouslyHiredColumn from './EmploymentPreviouslyHiredColumn.vue'
import EmploymentContractColumn from './EmploymentContractColumn.vue'
import EmploymentHiringManagerColumn from './EmploymentHiringManagerColumn.vue'
import EmploymentLocationColumn from './EmploymentLocationColumn.vue'
import EmploymentAssignmentCompanyColumn from './EmploymentAssignmentCompanyColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// Import GraphQL fragments and types
import {
  EmploymentWorkerColumnFragmentDoc,
  EmploymentJobColumnFragmentDoc,
  EmploymentStartDateColumnFragmentDoc,
  EmploymentEndDateColumnFragmentDoc,
  EmploymentOnboardedDateColumnFragmentDoc,
  EmploymentPreviouslyHiredColumnFragmentDoc,
  EmploymentContractColumnFragmentDoc,
  EmploymentHiringManagerColumnFragmentDoc,
  EmploymentLocationColumnFragmentDoc,
  EmploymentAssignmentCompanyColumnFragmentDoc,
  SortOrder,
  EmploymentOrderByColumn,
} from '@/gql/graphql'

// Import fragment types for type safety
import type {
  EmploymentWorkerColumnFragment,
  EmploymentJobColumnFragment,
  EmploymentStartDateColumnFragment,
  EmploymentEndDateColumnFragment,
  EmploymentOnboardedDateColumnFragment,
  EmploymentPreviouslyHiredColumnFragment,
  EmploymentContractColumnFragment,
  EmploymentHiringManagerColumnFragment,
  EmploymentLocationColumnFragment,
  EmploymentAssignmentCompanyColumnFragment,
} from '@/gql/graphql'

// Employment column definitions
const PartnerEmploymentColumns: ColumnMaps = {
  EmploymentWorkerColumn: {
    fragment: EmploymentWorkerColumnFragmentDoc,
    component: markRaw(EmploymentWorkerColumn),
    columnDef: {
      title: $t('Worker name and email'),
      key: 'worker',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentWorkerColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Worker name',
        extraction: (row: EmploymentWorkerColumnFragment) => row.worker?.name || 'No name',
      },
      {
        title: 'Worker email',
        extraction: (row: EmploymentWorkerColumnFragment) => row.worker?.email || 'No email',
      },
    ],
    sort: [
      {
        name: `${EmploymentOrderByColumn.WorkerName}_${SortOrder.Asc}`,
        orderByColumn: EmploymentOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Worker name'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the worker name'),
      },
      {
        name: `${EmploymentOrderByColumn.WorkerName}_${SortOrder.Desc}`,
        orderByColumn: EmploymentOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the worker name'),
      },
    ],
  },
  EmploymentJobColumn: {
    fragment: EmploymentJobColumnFragmentDoc,
    component: markRaw(EmploymentJobColumn),
    columnDef: {
      title: $t('Worksome Hire title and ID'),
      key: 'job',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentJobColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: EmploymentJobColumnFragment) =>
          row.hires?.data?.[0]?.latestContract?.jobName || 'No title',
      },
      {
        title: 'Hire Number',
        extraction: (row: EmploymentJobColumnFragment) =>
          row.hires?.data?.[0]?.number || 'No number',
      },
    ],
    sort: [
      {
        name: `${EmploymentOrderByColumn.JobName}_${SortOrder.Asc}`,
        orderByColumn: EmploymentOrderByColumn.JobName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Job title'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the job title'),
      },
      {
        name: `${EmploymentOrderByColumn.JobName}_${SortOrder.Desc}`,
        orderByColumn: EmploymentOrderByColumn.JobName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Job title'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the job title'),
      },
    ],
  },
  EmploymentStartDateColumn: {
    fragment: EmploymentStartDateColumnFragmentDoc,
    component: markRaw(EmploymentStartDateColumn),
    columnDef: {
      title: $t('Start date'),
      key: 'start_date',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentStartDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Start date',
        extraction: (row: EmploymentStartDateColumnFragment) =>
          row.hires.data[0].startDate || 'No start date',
      },
    ],
    sort: [
      {
        name: `${EmploymentOrderByColumn.StartDate}_${SortOrder.Asc}`,
        orderByColumn: EmploymentOrderByColumn.StartDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Start date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the start date'),
      },
      {
        name: `${EmploymentOrderByColumn.StartDate}_${SortOrder.Desc}`,
        orderByColumn: EmploymentOrderByColumn.StartDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Start date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the start date'),
      },
    ],
  },
  EmploymentEndDateColumn: {
    fragment: EmploymentEndDateColumnFragmentDoc,
    component: markRaw(EmploymentEndDateColumn),
    columnDef: {
      title: $t('End date'),
      key: 'end_date',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentEndDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'End date',
        extraction: (row: EmploymentEndDateColumnFragment) =>
          row.hires.data[0].endDate || 'No end date',
      },
    ],
    sort: [
      {
        name: `${EmploymentOrderByColumn.EndDate}_${SortOrder.Asc}`,
        orderByColumn: EmploymentOrderByColumn.EndDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('End date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the end date'),
      },
      {
        name: `${EmploymentOrderByColumn.EndDate}_${SortOrder.Desc}`,
        orderByColumn: EmploymentOrderByColumn.EndDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('End date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the end date'),
      },
    ],
  },
  EmploymentOnboardedDateColumn: {
    fragment: EmploymentOnboardedDateColumnFragmentDoc,
    component: markRaw(EmploymentOnboardedDateColumn),
    columnDef: {
      title: $t('Onboarded'),
      key: 'onboarded_date',
      renderUsing: {
        component: markRaw(EmploymentOnboardedDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Onboarded date',
        extraction: (row: EmploymentOnboardedDateColumnFragment) =>
          row.onboardedAt || 'Not onboarded',
      },
    ],
  },
  EmploymentPreviouslyHiredColumn: {
    fragment: EmploymentPreviouslyHiredColumnFragmentDoc,
    component: markRaw(EmploymentPreviouslyHiredColumn),
    columnDef: {
      title: $t('Previously hired'),
      key: 'previously_hired',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentPreviouslyHiredColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Previously hired',
        extraction: (row: EmploymentPreviouslyHiredColumnFragment) =>
          row.previouslyHired ? 'Yes' : 'No',
      },
    ],
    sort: [],
  },
  EmploymentContractColumn: {
    fragment: EmploymentContractColumnFragmentDoc,
    component: markRaw(EmploymentContractColumn),
    columnDef: {
      title: $t('Contract'),
      key: 'contract',
      orderAble: false,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(EmploymentContractColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Contract number',
        extraction: (row: EmploymentContractColumnFragment) =>
          row.hires?.data?.[0]?.latestContract?.id || 'No contract',
      },
    ],
  },
  EmploymentHiringManagerColumn: {
    fragment: EmploymentHiringManagerColumnFragmentDoc,
    component: markRaw(EmploymentHiringManagerColumn),
    columnDef: {
      title: $t('Hiring manager'),
      key: 'hiring_manager',
      renderUsing: {
        component: markRaw(EmploymentHiringManagerColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Hiring manager',
        extraction: (row: EmploymentHiringManagerColumnFragment) =>
          row.hiringManagers?.[0]?.name || 'No hiring manager',
      },
    ],
  },
  EmploymentLocationColumn: {
    fragment: EmploymentLocationColumnFragmentDoc,
    component: markRaw(EmploymentLocationColumn),
    columnDef: {
      title: $t('Worker Location'),
      key: 'location',
      renderUsing: {
        component: markRaw(EmploymentLocationColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Location',
        extraction: (row: EmploymentLocationColumnFragment) =>
          row.worker?.address?.country?.name || 'No location',
      },
    ],
  },
  EmploymentAssignmentCompanyColumn: {
    fragment: EmploymentAssignmentCompanyColumnFragmentDoc,
    component: markRaw(EmploymentAssignmentCompanyColumn),
    columnDef: {
      title: $t('Assignment company'),
      key: 'assignment_company',
      orderAble: true,
      renderUsing: {
        component: markRaw(EmploymentAssignmentCompanyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Assignment company',
        extraction: (row: EmploymentAssignmentCompanyColumnFragment) =>
          row.hires?.data?.[0]?.company?.name || 'No assignment company',
      },
    ],
    sort: [
      {
        name: `${EmploymentOrderByColumn.CompanyName}_${SortOrder.Asc}`,
        orderByColumn: EmploymentOrderByColumn.CompanyName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Assignment company'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the assignment company'),
      },
      {
        name: `${EmploymentOrderByColumn.CompanyName}_${SortOrder.Desc}`,
        orderByColumn: EmploymentOrderByColumn.CompanyName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Assignment company'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the assignment company'),
      },
    ],
  },
}

// Export the column definitions
export default PartnerEmploymentColumns
